import { motion } from 'framer-motion'
import {
  FileText,
  LayoutDashboard,
  Receipt,
  Route,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { MoreMenu } from '@/components/ui/MoreMenu'
import { Sheet } from '@/components/ui/Sheet'
import { TabPanel } from '@/components/ui/TabPanel'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { TAB_MARKER_TRANSITION } from '@/lib/motion'
import { brandingRefusalFields, brandingRefusalMessage } from '@/lib/refusals'
import { useCase, useCaseTeam, useSendCaseStatus } from '@/features/cases/api/useCase'
import { useSession } from '@/features/auth/api/useSession'
import {
  offeredCaseSections,
  resolveCaseSection,
  type CaseSectionKey,
} from '@/features/cases/caseSections'
import { CaseTeamSection } from '@/features/cases/components/CaseTeamSection'
import { CaseHeader } from '@/features/cases/components/CaseHeader'
import { CaseLifecycleActions } from '@/features/cases/components/CaseLifecycleActions'
import { CaseStatusSendSheet } from '@/features/cases/components/CaseStatusSendSheet'
import {
  BillingSection,
  FilesSection,
  HistorySection,
  LegsSection,
  MoneySection,
  OverviewSection,
} from '@/features/cases/components/CaseSections'
import { reveal, revealInRow } from '@/features/cases/lib/reveal'
import { ChatDock, ChatDockBadge } from '@/features/chat/components/ChatDock'
import {
  canSeeRecordChat,
  useRecordConversation,
} from '@/features/chat/api/useRecordChat'
import { useTranslation } from 'react-i18next'

/* Case detail.
 *
 * The section rail is driven by a `?section=` search param rather than a nested
 * route, so a section is linkable and survives a refresh while the sticky
 * header and the chat panel stay mounted across a switch — remounting the chat
 * every time someone checks the Legs tab would lose their scroll position and
 * their half-typed message.
 *
 * Chat is a FLOATING DOCK in the bottom-right corner on desktop and a
 * full-screen view on a phone (Story 8.3, FR-46, UX-DR-13, UX-DR-28, 0052). It
 * is not a tab: a conversation buried inside one section of one record is a
 * conversation people stop opening, which is the D1 failure the feature exists
 * to prevent. It IS dismissible now, which the desktop column it replaced was
 * not — the guarantee that carries D1's lesson moved to the pair "expanded by
 * default, and the minimised button still carries the unread count". ChatDock's
 * header comment is the record of that decision; read it before making the dock
 * start collapsed.
 *
 * The page itself is one column at every breakpoint. From xl up an EXPANDED
 * dock takes its width from the shell rather than from this page (N-06, #127):
 * AppShell's `<main>` pads itself by the dock while ChatDock carries its
 * `data-chat-dock="expanded"` marker, so the record reflows beside the
 * conversation instead of running under it, and gets the width back the moment
 * the dock is minimised or closed. The one thing here that answers to that is
 * the section rail, whose orientation follows the record's own width — see the
 * rail. Both shapes are one mount — see ChatDock for why that is what
 * guarantees phone parity rather than a promise of it.
 *
 * The panel itself is subject-agnostic now (0051): the same ChatDock the quote
 * detail renders, handed `kind="case"` and this record's id. It is shown only
 * to a reader the record's team actually contains, because chat is narrowed to
 * that team and every request from a panel shown to anyone else would 404.
 *
 * The page owns the PHONE view's open state rather than ChatDock, because the
 * badge that toggles it lives in the sticky header and the panel lives at the
 * end of the layout. One piece of state with two readers beats an imperative
 * handle passed between siblings. The DESKTOP dock's collapsed/expanded choice
 * is not page state at all — it is a remembered per-reader preference the dock
 * owns through `useChatDockState`, because it outlives this page. */

/* SIX SECTIONS (S-11, revised after the owner's review of 2026-10-16).
 *
 * «Обзор» is the summary, «Плечи» the legs, «Команда» the colleagues working
 * the case, «Счета клиенту» what the client is billed and has paid with
 * Finance's invoicing in it, «Затраты» the cost lines and what each supplier is
 * owed, «Документы» the case's paper and its files. The rule — which keys
 * exist, which reader is offered which, and where an old key lands — is
 * `features/cases/caseSections.ts`, pure and tested row by row; this page
 * renders what it answers.
 *
 * KEYS AND LABELS are allowed to disagree, and do: «Документы» is the `files`
 * key, because a key is a CONTRACT — pasted into chat threads, generated by the
 * app's own back links — and a label is a word on a screen. A rename to match a
 * label would make the screen and its links stop being traceable to each other.
 *
 * THE OLD KEYS ARE PERMANENT ALIASES, resolved on read and NEVER written back:
 * `money` opens «Затраты», `participants` «Команда», `documents` «Счета
 * клиенту» at the invoices for a reader given Finance's workspace (or
 * «Документы» for an archive-key holder, «Обзор» for anyone else), and
 * `history` opens «Обзор» with the history sheet. The section is DERIVED from
 * the URL on every render, never seeded into state, so a deep link waits for
 * the session and the record instead of being rewritten to the overview while
 * they load.
 *
 * «История» left the rail for the header's «⋯», as on the quote (S-3): a log
 * of what happened is about the case, not a part of the work on it. */
const SECTIONS: Record<
  CaseSectionKey,
  { labelKey: `cases.tabs.${CaseSectionKey}`; icon: LucideIcon }
> = {
  overview: { labelKey: 'cases.tabs.overview', icon: LayoutDashboard },
  legs: { labelKey: 'cases.tabs.legs', icon: Route },
  team: { labelKey: 'cases.tabs.team', icon: Users },
  billing: { labelKey: 'cases.tabs.billing', icon: Receipt },
  costs: { labelKey: 'cases.tabs.costs', icon: Wallet },
  files: { labelKey: 'cases.tabs.files', icon: FileText },
}

export function CaseDetailPage() {
  const { t } = useTranslation()
  const { caseId = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [chatOpen, setChatOpen] = useState(false)
  /* Set by the quote conversion when the case arrived without its planned leg
     (ConversionResult.legCreated false): the selected route option had no
     transport mode, and a leg cannot exist without one. */
  const legSkipped =
    (useLocation().state as { legSkipped?: boolean } | null)?.legSkipped === true
  const [statusOpen, setStatusOpen] = useState(false)
  /* What the last send reached, held so the confirmation can NAME the address
     rather than saying "sent" and leaving the sender to trust it. Cleared when
     the sheet reopens, because a stale success above a fresh form reads as if
     this send already went. */
  const [sentTo, setSentTo] = useState<string | null>(null)

  const { data: record, isPending, isError, error, refetch } = useCase(caseId)
  const sendStatus = useSendCaseStatus(caseId)

  /* Chat narrowed to the record's TEAM (0051), so a reader who is not on this
     case would get a 404 from every chat request. Deciding it here keeps the
     panel from advertising a door that does not open — the server refuses
     regardless; this is about not offering.

     The owner id IS handed over, and passing an empty string here was a bug
     for as long as this line existed. The reasoning it replaced was that the
     owner is seeded onto `case_team` at create (migration 0007), so the team
     read would always contain them — but casefile.Service.Unassign refuses only
     the removal that would EMPTY a team, so an owner can be taken off their own
     case while a colleague remains. In that state StaffedOrOwned still serves
     them the conversation (`c.owner_id = $1 OR` the team predicate) and this
     page rendered no panel and no badge at all.

     `record` is read optionally: these hooks run before the loading and error
     branches return, because hooks cannot be called conditionally. The team
     query answers on its own and does not wait for the detail — so the owner
     id may be undefined for one render, which reads as "not yet" rather than
     as "no", exactly as an unresolved team does. */
  const { data: user } = useSession()
  const team = useCaseTeam(caseId)
  const canChat = canSeeRecordChat(
    user,
    record?.ownerId ?? '',
    team.data ?? record?.team ?? [],
  )

  /* The conversation's row, from the list the chat page already loads. It is
     where both facts the page needs come from: the id the socket subscribes to,
     and the unread count the phone badge renders. A case nobody has spoken on
     has no row, which is the ordinary state and not an error. */
  const conversation = useRecordConversation('case', caseId, canChat)
  const chatUnread = conversation?.unread ?? 0

  /* Which section the URL asks for, through the alias table. `pending` while
     the session or the record loads: the page renders no section body then
     (the record's own skeleton below covers the second). */
  const moneyForbidden = record?.moneyUnavailable === 'forbidden'
  const requested = searchParams.get('section')
  const resolved = resolveCaseSection(requested, {
    user,
    moneyForbidden,
    recordLoaded: record !== undefined,
  })
  const sections = offeredCaseSections(moneyForbidden)
  const section = resolved.kind === 'section' ? resolved.key : undefined
  const anchor = resolved.kind === 'section' ? resolved.anchor : undefined

  /* The history sheet's open state is DERIVED, never seeded in an effect: open
     while the URL's alias asks for it (`?section=history`) or while the reader
     opened it from «⋯». Opening from the menu writes nothing to the URL;
     closing clears the menu's half and, when the URL still says `history`,
     replaces it with `overview` — no new browser-history entry, and the sheet
     does not reopen. */
  const [historyFromMenu, setHistoryFromMenu] = useState(false)
  const historyOpen =
    (resolved.kind === 'section' && resolved.openHistory === true) || historyFromMenu
  const closeHistory = () => {
    setHistoryFromMenu(false)
    if (requested === 'history') {
      const next = new URLSearchParams(searchParams)
      next.set('section', 'overview')
      setSearchParams(next, { replace: true })
    }
  }

  /* The ONE-SHOT reveal of the block an alias lands on — the invoices under
     «Счета клиенту», for `?section=documents` — once per resolved section and
     anchor, once the anchor exists. `reveal` scrolls the shell's container
     under the sticky header and focuses the block — never `scrollIntoView`,
     which drags the whole shell up in Chrome. A plain key has no anchor and
     scrolls nothing. A ref, not state: it remembers what was done and changes
     no render.

     Nothing is awaited first. The invoices used to sit under the money block's
     payments list, and a reveal measured before that list arrived landed
     wherever its growth then pushed the heading, so the page read the payments
     to time it. «Полученные платежи» sits BELOW the invoices now (S-11
     revision), so nothing above the anchor arrives late. */
  const revealed = useRef<string | null>(null)
  useEffect(() => {
    if (anchor === undefined || section === undefined) {
      revealed.current = null
      return
    }
    const token = `${section}#${anchor}`
    if (revealed.current === token) return
    const element = document.getElementById(anchor)
    if (!element) return
    revealed.current = token
    reveal(element)
  }, [anchor, section])

  /* The phone's switcher scrolls sideways inside its own card (S-11 revision:
     six text tabs do not fit 390px), so the open section is brought into the
     ROW's view whenever it changes — a deep link to «Документы» must not open
     with its own tab scrolled out of sight. `revealInRow` moves the row's
     `scrollLeft` alone. The same holds for the horizontal rail a desktop shows
     beside an expanded chat dock (N-06); the vertical rail does not scroll, and
     nothing moves. */
  const railRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const row = railRef.current
    const active = row?.querySelector<HTMLElement>('[aria-current="page"]')
    if (row && active) revealInRow(row, active)
  }, [section])

  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('cases.loading')}>
        <Skeleton className="h-24 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-10 w-full max-w-lg rounded-md" />
        <Skeleton className="h-72 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  if (isError) {
    return <ErrorState error={error} onRetry={() => void refetch()} />
  }

  const filesPending = record.files.filter((file) => file.pending).length

  /* What a refused send SAYS, and the one refusal that must not be worded as a
     retry.
     `cases.statusSend.failed` — "check the connection and try again" — is the
     honest sentence for what this endpoint usually fails on: a dead network, a
     500, a shipment that moved status while the sheet was open. It is the wrong
     sentence for exactly one refusal. FR-64's branding gap comes back as a 422
     naming the ISSUING ENTITY's missing fields (internal/casefile's `fail`,
     which answers what internal/quote and internal/document already answered
     for the identical error), and retrying fixes none of it — the cure is a
     settings form nobody filled in, on a screen this page never mentions.
     Read through `lib/refusals` rather than by inspecting `problem.errors`
     here: the offer sheet, the invoice sheet and this case's own document card
     all name these fields from the same document, and a fourth reading of one
     wire shape is how two surfaces end up disagreeing about which field was
     missing. `brandingRefusalFields` answers null for everything else, which is
     what keeps the sentence above intact for every other failure — this is not
     the generic problem mapping quietly taking the message over.
     One more refusal is not a retry either (story CI, R9/R10): a 403 means
     the caller lacks `directory.contact.read` — the send would hand them the
     client's address — and the sheet already says so up front; this is the
     stale tab whose role was narrowed after the page loaded. It is worded as
     who can send, never as "check the connection". */
  const sendRefusal = brandingRefusalFields(sendStatus.error)
  const sendForbidden = sendStatus.error instanceof ApiError && sendStatus.error.isForbidden

  /* Record counts on the rail, so "is anything there yet?" is answered without
     visiting the section: the legs on «Плечи», the colleagues on «Команда»
     (the team read, the record's own list until it answers). «Документы»
     shows PENDING uploads instead (attention), not a total — a total is
     trivia, an unconfirmed upload is a task. */
  const counts: Partial<Record<CaseSectionKey, number>> = {
    legs: record.legs.length,
    team: (team.data ?? record.team ?? []).length,
  }

  return (
    /* ONE COLUMN, at every width. This used to be a flex ROW above lg, with the
       record on the left and a persistent chat column on the right — which cost
       the case roughly 306px of its width on every screen, whether or not the
       reader was in the conversation. The dock is fixed to the viewport now
       (0052), so it is out of this page's flow: `ChatDock` below is a sibling
       of the record's content only for z-order. The width an expanded dock
       needs from xl up is reserved by the SHELL while the dock is expanded
       (N-06, #127), never by this page, so this stays one column. */
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <CaseHeader
          record={record}
          onSendStatus={() => {
            sendStatus.reset()
            setSentTo(null)
            setStatusOpen(true)
          }}
          /* The lifecycle surface (FR-25, FR-37), composed here rather than
             inside the header so the header keeps owning no mutation. It reads
             the same `record` the header does, so the moves it offers and the
             status pill beside them can never disagree. */
          lifecycle={<CaseLifecycleActions record={record} />}
          chatBadge={
            canChat ? (
              <ChatDockBadge onOpen={() => setChatOpen(true)} unread={chatUnread} />
            ) : null
          }
          /* The history's way in since it left the rail (S-11): the quote's
             «⋯», shared, with one item. */
          more={
            <MoreMenu
              size="sm"
              label={t('cases.more.label')}
              items={[{ key: 'history', label: t('cases.more.history') }]}
              onSelect={() => setHistoryFromMenu(true)}
            />
          }
        />
        {legSkipped ? <Banner tone="info" title={t('cases.conversionLegSkipped')} /> : null}

        {/* The confirmation lives on the PAGE rather than inside the sheet,
            because the sheet closes on success: a message shown only there
            would be shown only to a form nobody is looking at any more. It
            names the address, so "sent" is a fact the sender can check rather
            than a reassurance. */}
        {sentTo ? (
          <div
            role="status"
            className="border-positive/30 bg-positive-soft text-positive rounded-[var(--radius-md)] border px-4 py-2.5 text-[13px]"
          >
            {t('cases.statusSend.sent', { email: sentTo })}
          </div>
        ) : null}

        {/* The secondary rail. Beside the content as a vertical list on
            desktop, a horizontal switcher on a phone — and ALSO a horizontal
            switcher on desktop whenever the record itself is narrower than
            45rem, which is what an expanded chat dock does to it at 1280 and
            1440 (N-06, #127: AppShell gives the dock its width). So the
            orientation is a CONTAINER query on the record's own width, gated
            on lg so the phone keeps its `max-sm:`/`max-lg:` shape exactly.
            45rem sits below the narrowest lg record without the dock (~730px
            at 1024), so every desktop layout without a dock is unchanged.

            The container is this wrapper and not the page root on purpose: a
            size container may become the containing block of its fixed
            descendants, and ChatDock below must stay fixed to the viewport. */}
        <div className="@container/record min-w-0">
        <div className="flex flex-col gap-4 lg:@min-[45rem]/record:flex-row lg:@min-[45rem]/record:items-start lg:@min-[45rem]/record:gap-6">
          <nav
            ref={railRef}
            aria-label={t('cases.sectionsLabel')}
            className={cn(
              'asa-scroll bg-surface border-line flex min-w-0 shrink-0 gap-0.5 self-start',
              'overflow-x-auto rounded-[var(--radius-lg)] border p-1.5',
              'max-lg:self-stretch lg:@max-[45rem]/record:self-stretch',
              'lg:@min-[45rem]/record:w-48 lg:@min-[45rem]/record:flex-col',
              'lg:@min-[45rem]/record:overflow-visible',
            )}
          >
            {sections.map((key) => {
              const item = SECTIONS[key]
              const active = key === section
              const badge = key === 'files' ? filesPending : 0
              const count = counts[key]
              const Icon = item.icon

              return (
                <button
                  key={key}
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams)
                    next.set('section', key)
                    setSearchParams(next, { replace: true })
                  }}
                  className={cn(
                    'group relative flex shrink-0 cursor-pointer items-center gap-2.5',
                    'rounded-[var(--radius-md)] px-3 py-2.5 text-[13px]',
                    'transition-colors duration-[var(--dur-fast)]',
                    'ease-[var(--ease-standard)] lg:@min-[45rem]/record:w-full',
                    'lg:@min-[45rem]/record:justify-start',
                    /* A phone (S-11 revision): one row of text tabs — no icon,
                       no quiet count, only the attention badge. Six do not fit
                       390px, so the row scrolls sideways inside its own card
                       and a label never wraps; where they do fit, they share
                       the width. */
                    'whitespace-nowrap',
                    'max-sm:flex-1 max-sm:justify-center max-sm:gap-1.5 max-sm:px-2',
                    active
                      ? 'text-text-heading font-medium'
                      : 'text-text-muted hover:bg-surface-hover hover:text-text-heading font-normal',
                  )}
                >
                  {/* The rail is a white hairline card, so the selected state
                      is a soft brand tint inside it. The brand accent stays on
                      the icon, not the label, so blue stays scarce.

                      The pill is its own element with a shared layoutId, so
                      Framer slides it between sections instead of repainting
                      one background off and another on. It works unchanged in
                      both orientations — the rail is horizontal below lg (and
                      on a desktop record narrowed by the chat dock) and
                      vertical otherwise, and the layout engine interpolates
                      whichever axis actually moved.

                      The pill is painted first and the label is lifted above it
                      with its own stacking context, rather than pushing the
                      pill behind with a negative z-index — negative would put
                      it behind the button itself and the fill would vanish. */}
                  {active ? (
                    <motion.span
                      layoutId="case-section-marker"
                      transition={TAB_MARKER_TRANSITION}
                      aria-hidden
                      className="bg-brand-soft absolute inset-0 rounded-[var(--radius-md)]"
                    />
                  ) : null}

                  <Icon
                    size={16}
                    aria-hidden
                    className={cn(
                      'relative shrink-0 transition-colors duration-[var(--dur-fast)] max-sm:hidden',
                      active ? 'text-brand' : 'text-text-faint group-hover:text-text-muted',
                    )}
                  />
                  <span className="relative">{t(item.labelKey)}</span>

                  {/* Pending files are a task and keep the attention badge;
                      plain record counts are quiet chips at the rail edge. */}
                  {badge > 0 ? (
                    <span className="bg-attention-soft text-attention asa-num relative rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold lg:@min-[45rem]/record:ml-auto">
                      {badge}
                    </span>
                  ) : count ? (
                    <span className="asa-num bg-surface border-line text-text-muted relative rounded-full border px-1.5 py-0.5 text-[10.5px] leading-none font-normal max-sm:hidden lg:@min-[45rem]/record:ml-auto">
                      {count}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </nav>

          {/* No body while the section is `pending` — the session is still
              loading — rather than one it would have to replace. */}
          {section === undefined ? null : (
            <TabPanel tabKey={section} className="min-w-0 flex-1">
              {section === 'overview' ? <OverviewSection record={record} /> : null}
              {/* The legs on their own tab again (S-11 revision), the chain
                  exactly as it rendered under «Обзор». */}
              {section === 'legs' ? <LegsSection record={record} /> : null}
              {/* FR-69 still puts assignment ON THE RECORD rather than on a
                  settings screen — a section of the case is on the record. The
                  parties to the shipment are no longer rendered here (S-11,
                  issue #97). */}
              {section === 'team' ? <CaseTeamSection record={record} /> : null}
              {/* What the client is billed and has paid; for a reader given
                  Finance's workspace, the invoices under the `case-invoices`
                  anchor `?section=documents` lands on. */}
              {section === 'billing' ? (
                <BillingSection record={record} onRetry={() => void refetch()} />
              ) : null}
              {/* The costs and what each supplier is owed. `onRetry` is the
                  page's refetch because the block rides the composed detail —
                  the section has no query of its own to retry. */}
              {section === 'costs' ? (
                <MoneySection record={record} onRetry={() => void refetch()} />
              ) : null}
              {section === 'files' ? <FilesSection record={record} /> : null}
            </TabPanel>
          )}
        </div>
        </div>

      {/* Sending the client their status (FR-41). The sheet stays open on
          failure holding the note, so nothing typed is lost to a refused send
          (UX-DR-18) — and closes only once the server has accepted it, because
          an email cannot be unsent and "sent" must mean sent. */}
      <CaseStatusSendSheet
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        record={record}
        user={user}
        pending={sendStatus.isPending}
        submitError={
          !sendStatus.isError
            ? undefined
            : sendRefusal !== null
              ? brandingRefusalMessage(sendRefusal, t)
              : sendForbidden
                ? t('cases.statusSend.noContactAccess')
                : t('cases.statusSend.failed')
        }
        onSubmit={(values) => {
          sendStatus.mutate(values, {
            onSuccess: (result) => {
              setSentTo(result.sentTo)
              setStatusOpen(false)
            },
          })
        }}
      />

      {/* The history, in a side sheet from «⋯» (S-11) — the quote's
          arrangement. The sheet mounts its body only while open, so the two
          history reads are issued only then. The card inside drops its own
          «История» title: the sheet's is the one heading. The case id, not a
          slice of the record: the feed reads the action log and the financial
          trail itself. The trail is money, so a case whose money this reader
          may not reach reads the log alone (story B3, fix round 1). */}
      <Sheet open={historyOpen} onClose={closeHistory} title={t('cases.history.title')}>
        <HistorySection caseId={record.id} moneyForbidden={moneyForbidden} headless />
      </Sheet>

      {/* The conversation. Fixed-positioned in both its shapes — a dock in
          the bottom-right corner above lg, a full-screen layer below it — so it
          takes part in no layout HERE and its position in this tree is only
          about z-order and about being a sibling rather than a child of
          something that scrolls (or of the rail's size container). Expanded at
          xl and up, it is still sized around: by AppShell's `<main>`, which
          reserves its width while it carries its marker (N-06, #127). See
          ChatDock for why the two shapes are one mount.

          The number and the client come off the record this page already holds
          rather than being fetched again: they are the dock's subtitle, and one
          request for two strings would be a request too many.

          `unread` is the same count the header badge renders, from the same
          conversation row — the phone badge and the desktop dock's minimised
          button must never disagree about whether somebody has said something.

          Not rendered at all for a reader who is not on this case: every
          request it made would answer 404 (0051). */}
      {canChat ? (
        <ChatDock
          kind="case"
          subjectId={record.id}
          conversationId={conversation?.id}
          subjectNumber={record.number}
          subjectClient={record.clientName}
          unread={chatUnread}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          allowAttachments
          sourceQuoteId={record.quoteId}
        />
      ) : null}
    </div>
  )
}
